import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '@/components/common/Image';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import CircularText from '@/components/Shared/CircularText';
import aiSystemsImage from '@/assets/work-carousel/crossroads.png';
import productDesignImage from '@/assets/work-carousel/babelcast.png';
import webDevelopmentImage from '@/assets/work-carousel/straata.png';

interface ServiceItem {
  id: number;
  title: string;
  image: string;
  alt: string;
}

const services: ServiceItem[] = [
  {
    id: 1,
    title: 'AI Systems',
    image: aiSystemsImage,
    alt: 'AI systems operations workstation',
  },
  {
    id: 2,
    title: 'Product Design',
    image: productDesignImage,
    alt: 'Product design analytics dashboard',
  },
  {
    id: 3,
    title: 'Web Development',
    image: webDevelopmentImage,
    alt: 'Web development metrics dashboard',
  },
];

const InteractiveServicesSection: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // State to track hover specifically on the 4th column's default content
  const [isDefaultHovered, setIsDefaultHovered] = useState(false);

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    setIsDefaultHovered(false); // Ensure default hover is off when a service is hovered
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    // Don't reset isDefaultHovered here, it's handled by its own mouse events
  };

  const handleDefaultMouseEnter = () => {
    if (hoveredIndex === null) {
      // Only trigger if no service column is active
      setIsDefaultHovered(true);
    }
  };

  const handleDefaultMouseLeave = () => {
    setIsDefaultHovered(false);
  };

  const currentService = hoveredIndex !== null ? services[hoveredIndex] : null;

  return (
    <div className="relative grid min-h-screen grid-cols-1 md:grid-cols-4 w-full bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 border-t border-zinc-200 dark:border-zinc-800">
      {/* Service Columns (1-3) */}
      {services.map((service, index) => (
        <motion.div
          key={service.id}
          className="relative flex flex-col justify-center items-center p-8 border-r border-zinc-200 dark:border-zinc-800 last:border-r-0 md:last:border-r cursor-pointer overflow-hidden h-full min-h-[25vh] md:min-h-full"
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        >
          {/* Animated Background Overlay */}
          <motion.div
            className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 origin-center z-0"
            initial={{ scale: 0 }}
            animate={{ scale: hoveredIndex === index ? 1 : 0 }}
            transition={{ duration: 0.4, ease: 'circOut' }}
          />

          {/* Content */}
          <div className="relative z-10 flex items-center gap-3 transition-colors duration-300">
            {/* Apply conditional text colors directly */}
            <motion.span
              className={`text-2xl sm:text-3xl font-light transition-colors duration-300 ${
                hoveredIndex === index
                  ? 'text-zinc-100 dark:text-zinc-900' // Hover state colors
                  : 'text-zinc-900 dark:text-zinc-100' // Default state colors
              }`}
              // Animate prop removed - handled by className toggle + transition
            >
              {service.title}
            </motion.span>
            <motion.div
              className={`transition-colors duration-300 ${
                hoveredIndex === index
                  ? 'text-zinc-100 dark:text-zinc-900' // Hover state colors
                  : 'text-zinc-900 dark:text-zinc-100' // Default state colors
              }`}
              initial={false}
              animate={{ rotate: hoveredIndex === index ? 45 : 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <ArrowUpRightIcon className="w-6 h-6 sm:w-8 sm:h-8" />
            </motion.div>
          </div>
        </motion.div>
      ))}

      {/* Image Display Column (4) */}
      {/* Add hover handlers to the container */}
      <div
        className="relative flex justify-center items-center h-full min-h-[40vh] md:min-h-full bg-white dark:bg-zinc-950 md:bg-transparent dark:md:bg-transparent overflow-hidden"
        onMouseEnter={handleDefaultMouseEnter}
        onMouseLeave={handleDefaultMouseLeave}
      >
        {/* Animated Background Overlay for default state */}
        <motion.div
          className="absolute inset-0 bg-zinc-900 dark:bg-zinc-100 origin-center z-0"
          initial={{ scale: 0 }}
          // Animate only when default is hovered AND no service is selected
          animate={{ scale: isDefaultHovered && hoveredIndex === null ? 1 : 0 }}
          transition={{ duration: 0.4, ease: 'circOut' }}
        />

        <AnimatePresence mode="wait">
          {currentService ? (
            <motion.div
              key={currentService.id}
              className="relative z-10 h-full w-full"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              <Image
                src={currentService.image}
                alt={currentService.alt}
                layout="fill"
                objectFit="cover"
                className="h-full w-full object-cover"
              />
            </motion.div>
          ) : (
            // Wrap default state content for hover effects
            <motion.div
              key="circular-text-container"
              className="relative z-10 flex justify-center items-center w-full h-full cursor-pointer px-10 py-12 sm:px-14 sm:py-16" // Add cursor-pointer and relative z-10
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
            >
              {/* Container div - Keep this for layout, remove text color/font classes */}
              <div className="w-40 h-40 sm:w-56 sm:h-56 text-center flex justify-center items-center">
                {/* Pass isDefaultHovered state to CircularText */}
                <CircularText
                  text="View Our Services • View Our Services • "
                  isHovered={isDefaultHovered} // Pass hover state
                >
                  {/* Center dot color flip remains here */}
                  <div
                    className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                      isDefaultHovered
                        ? 'bg-zinc-100 dark:bg-zinc-900'
                        : 'bg-zinc-900 dark:bg-zinc-100'
                    }`}
                  ></div>
                </CircularText>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default InteractiveServicesSection;
